import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GetDeletedProfession, GetProfessions } from "./thunk";
import { IInitialStateProfessionsState, IProfessionPayload } from "../types";

const initialState: IInitialStateProfessionsState = {
    professionsData: [],
    isLoadingProfessions: true,
    isViewProfessionsRestore: false,
    professionCurrentPage: 0,
    professionTotalPage: 0,
    professionPagesSize: 0,
    professionsRestoreData: [],
    professionsSelectedIDs: [],
    filteredProfession: [],
};

const professionsSlice = createSlice({
    name: "professions",
    initialState,
    reducers: {
        setProfessionalsData: (
            state,
            action: PayloadAction<{
                data: IProfessionPayload[];
                totalPages: number;
                currentPage: number;
                pagesSize: number;
                defaultLanguage: string;
            }>
        ) => {
            const { currentPage, totalPages, data, pagesSize, defaultLanguage } = action.payload;

            state.professionCurrentPage = currentPage;
            state.professionTotalPage = totalPages;
            state.professionPagesSize = pagesSize;
            state.professionsData = data;

            state.filteredProfession = data.flatMap((profession: any) => {
                return profession.translations
                    .filter((item: any) => item.slug === defaultLanguage)
                    .map((elem: any) => ({
                        title: `${elem.profession_name} - ${elem.abbreviation}`,
                        id: profession.id,
                        value: `profession-${profession.id}`,
                        key: `profession-${profession.id}`,
                        children: profession.educational_programs.flatMap((educational: any) => {
                            return educational.translations
                                .filter((item: any) => item.slug === defaultLanguage)
                                .map((elem: any) => ({
                                    title: elem.program_name,
                                    value: `educational-${elem.educational_program_id}`,
                                    key: elem.educational_program_id,
                                    id: educational.id,
                                    children: educational.child_programs.flatMap((child: any) => {
                                        return child.translations
                                            .filter((item: any) => item.slug === defaultLanguage)
                                            .map((child: any) => ({
                                                title: child.child_program_name,
                                                value: `child-${child.child_educational_program_id}`,
                                                key: `child-${child.child_educational_program_id}`,
                                                id: child.child_educational_program_id,
                                            }));
                                    }),
                                }));
                        }),
                    }));
            });
        },

        deletedProfessions: (state, action: PayloadAction<number>) => {
            state.professionsData = state.professionsData.filter(elem => elem.id !== action.payload);
        },
        updateProfessions: (state, action: PayloadAction<IProfessionPayload>) => {
            const updateData = action.payload;

            state.professionsData = state.professionsData.map(elem => {
                if (elem.id === updateData.id) {
                    return {
                        ...elem,
                        ...updateData,
                    };
                }
                return elem;
            });
        },

        setProfessionsRestoreData: (state, action) => {
            state.professionsRestoreData = action.payload;
        },
        setIsViewProfessionsRestore: state => {
            state.isViewProfessionsRestore = !state.isViewProfessionsRestore;
        },
        setSelectedProfessionsIDs: (state, action) => {
            state.professionsSelectedIDs = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(GetProfessions.pending, state => {
                state.isLoadingProfessions = true;
            })
            .addCase(GetProfessions.fulfilled, state => {
                state.isLoadingProfessions = false;
            })
            .addCase(GetProfessions.rejected, state => {
                state.isLoadingProfessions = false;
            })
            .addCase(GetDeletedProfession.pending, state => {
                state.isLoadingProfessions = true;
            })
            .addCase(GetDeletedProfession.fulfilled, state => {
                state.isLoadingProfessions = false;
            })
            .addCase(GetDeletedProfession.rejected, state => {
                state.isLoadingProfessions = false;
            });
    },
});
export const {
    setProfessionalsData,
    deletedProfessions,
    updateProfessions,
    setProfessionsRestoreData,
    setIsViewProfessionsRestore,
    setSelectedProfessionsIDs,
} = professionsSlice.actions;

export default professionsSlice;
